import {
    type V2TermsVersionCreateRequestDto,
    type V2TermsVersionUpdateRequestDto,
    type V2TermsVersionReadResponseDto,
    useV2TermsControllerFetchTermsVersionDetail,
    useV2TermsControllerCreateTermsVersion,
    useV2TermsControllerUpdateTermsVersion,
    useV2TermsControllerDeleteTermsVersion,
} from "@everon-it/api-client/contents-gw";
import useAuthStore from "@store/auth";
import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import { RangeDateWrap } from "@components/atoms/DatePicker";
import IconBox from "@components/atoms/IconBox";
import Editor from "@components/molecules/Editor";
import ExcelButton from "@components/molecules/ExcelButton";
import useGlobalModal from "@hooks/useGlobalModal";
import useGlobalNoti from "@hooks/useGlobalNoti";
import { type ModalType } from "@store/modal";
import useTermsDataStore from "@store/terms";
import { colors, typo } from "@styles/index";
import { excelDownload } from "@utils/excel-download";
import { DateUtil } from "@everon-it/core";
import { htmlToText } from "html-to-text";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import styled, { css } from "styled-components";
import { DatePicker } from "antd";
import { type Row } from "@apis/common/types";
import BaseModal from "../BaseModal";
import OneButtonModal from "../OneButtonModal";
import ReasonEditModal from "../ReasonEditModal";
import ReasonModificationModal from "../ReasonModificationModal";
import TwoButtonModal from "../TwoButtonModal";
import isEditCheck from "./util";

type IsEditType = "active" | "future" | "past" | undefined;

export type TermsEditorModalProps = {
    title: string;
    termsUuid?: string;
    modalType?: ModalType;
};

function TermsEditorModal({ termsUuid, title, modalType = "TermsEditorModal" }: TermsEditorModalProps): JSX.Element {
    const { showDeleteNoti, showConfirmNoti, showEditNoti } = useGlobalNoti();
    const { hideModal } = useGlobalModal();
    const { userInfo } = useAuthStore();
    const { categoryInfo } = useTermsDataStore(state => state);
    const [termsData, setTermsData] = useState<Partial<V2TermsVersionReadResponseDto>>({
        uuid: "",
        termsTitle: "",
        content: "",
        effectiveStartAt: DateUtil.getToday(),
        effectiveEndAt: "",
        category: "",
        updateHistory: [],
        reason: "",
        updatedBy: "",
    });
    const [isEdit, setIsEdit] = useState<IsEditType>(undefined);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState({
        isOpenState: false,
        modalText: "",
    });
    const modalTitleState = isEdit === undefined ? "등록" : "상세";
    // 수정 사유 확인 모달 STATE
    const [isReasonModificationModalOpen, setIsReasonModificationModalOpen] = useState(false);
    // 수정 사유 입력 모달 STATE
    const [isReasonEditModal, setIsReasonEditModal] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const DATA_FORMAT = "YYYY-MM-DD";
    const queryClient = useQueryClient();

    // api-client: 상세 버전 조회
    const { data: detailData } = useV2TermsControllerFetchTermsVersionDetail<Row<V2TermsVersionReadResponseDto>>(termsUuid ?? "", {
        query: {
            enabled: !!termsUuid,
            retry: false,
            refetchOnWindowFocus: false,
        },
    });
    const initData = detailData?.row ?? undefined;

    // 상세 데이터 로드 시 처리
    useEffect(() => {
        if (initData) {
            const { effectiveStartAt, effectiveEndAt } = initData;
            setIsEdit(isEditCheck(effectiveStartAt, effectiveEndAt ?? ""));

            const newTermsData = {
                ...initData,
                effectiveStartAt: DateUtil.toDateString(effectiveStartAt),
                effectiveEndAt: effectiveEndAt ? DateUtil.toDateString(effectiveEndAt) : "",
            };
            setTermsData(newTermsData);
        }
    }, [initData]);

    // api-client: 약관 버전 등록
    const { mutate: termsVersionCreate } = useV2TermsControllerCreateTermsVersion({
        mutation: {
            onSuccess: () => {
                hideModal();
                showConfirmNoti({ text: "이력이 등록되었어요" });
                void queryClient.invalidateQueries({ queryKey: ["/v2/terms/versions"] });
            },
        },
    });

    // api-client: 약관 버전 수정
    const { mutate: termsVersionUpdate } = useV2TermsControllerUpdateTermsVersion({
        mutation: {
            onSuccess: () => {
                showEditNoti({ text: "이력이 수정되었어요." });
                void queryClient.invalidateQueries({ queryKey: ["/v2/terms/versions"] });
            },
        },
    });

    // api-client: 약관 버전 삭제
    const { mutate: termsVersionDelete } = useV2TermsControllerDeleteTermsVersion({
        mutation: {
            onSuccess: () => {
                showDeleteNoti({ text: "이력이 삭제되었어요." });
                void queryClient.invalidateQueries({ queryKey: ["/v2/terms/versions"] });
            },
        },
    });

    const onChangeStartAt = (_date: unknown, dateString: string) => {
        setTermsData(prev => ({
            ...prev,
            effectiveStartAt: dateString,
        }));
    };

    const onChangeEndAt = (_date: unknown, dateString: string) => {
        setTermsData(prev => ({
            ...prev,
            effectiveEndAt: dateString,
        }));
    };

    const handleDeleteButtonClick = () => {
        setIsDeleteModalOpen(true);
    };

    const handleCreateButtonClick = () => {
        const { content, effectiveEndAt, effectiveStartAt } = termsData;

        if (!categoryInfo.category) {
            return;
        }

        const body: V2TermsVersionCreateRequestDto = {
            category: categoryInfo.category,
            content: content ?? "",
            effectiveEndAt: effectiveEndAt ?? undefined,
            effectiveStartAt: effectiveStartAt ?? "",
            createdBy: userInfo?.name ?? "",
        };
        termsVersionCreate({ data: body });
    };

    const handleCloseModal = () => {
        hideModal();
    };

    // 수정 사유 확인 모달 열기
    const onReasonModificationButtonClick = () => {
        setIsReasonModificationModalOpen(true);
    };

    // 수정 사유 입력 모달 열기
    const onEditModalOpen = () => {
        if (!termsData.uuid) {
            return;
        }
        const { content, effectiveEndAt, effectiveStartAt } = termsData;

        // 현재 게시중인 약관인 경우 날짜만 수정 가능, reason 값이 contents-gw는 옵셔널인데 rest에는 필수여서 수정 사유는 날짜 변경 내역 전송
        if (isEdit === "active") {
            const prevEndAt = DateUtil.toDateString(initData?.effectiveEndAt);
            const newEndAt = effectiveEndAt;

            const body: V2TermsVersionUpdateRequestDto = {
                content,
                effectiveEndAt: effectiveEndAt ?? undefined,
                effectiveStartAt: effectiveStartAt ?? undefined,
                reason: `게시 종료일 ${prevEndAt} → ${newEndAt} 로 변경`,
                updatedBy: userInfo?.name ?? "",
            };
            termsVersionUpdate({ uuid: termsData.uuid, data: body });

            hideModal();
            return;
        }
        setIsReasonEditModal(true);
    };

    // 수정 사유 입력
    const handleChangeReason = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTermsData(prev => ({
            ...prev,
            reason: e.target.value,
        }));
    };

    // 수정 사유 입력 모달 저장하기 버튼
    const handleReasonConfirm = () => {
        setIsReasonEditModal(false);

        const { content, effectiveEndAt, effectiveStartAt, reason } = termsData;
        if (isEdit === "future" || isEdit === "active") {
            if (!termsData.uuid) {
                return;
            }

            const body: V2TermsVersionUpdateRequestDto = {
                content,
                effectiveEndAt: effectiveEndAt ?? undefined,
                effectiveStartAt,
                updatedBy: userInfo?.name ?? "",
                reason: reason ?? undefined,
            };
            termsVersionUpdate({ uuid: termsData.uuid, data: body });
        }

        hideModal();
    };

    const handleEditorChange = (content: string) => {
        setTermsData(prev => ({
            ...prev,
            content,
        }));
    };

    // 수정 사유 모달 닫기
    const handleReasonClose = () => {
        setIsReasonEditModal(false);
    };

    // 삭제 버튼 클릭
    const onDeleteModalClose = () => {
        setIsDeleteModalOpen(false);
    };

    // 삭제 모달 확인 버튼
    const handleDeleteModalConfirm = () => {
        if (!termsData.uuid) {
            return;
        }
        setIsDeleteModalOpen(false);
        termsVersionDelete({ uuid: termsData.uuid });
        hideModal();
    };

    const onExcelDownload = () => {
        const header = ["구분", "최초 등록 일시", "수정 변경 일시", "내용", "작성자"];
        const colsWidth = [100, 180, 180, 700, 150];
        // const rowsHeight = [0, 0];

        const excelData = {
            title: termsData.termsTitle ?? "",
            createAt: DateUtil.toDateTimeString(termsData.createdAt),
            updateAt: DateUtil.toDateTimeString(termsData.updatedAt ?? termsData.createdAt),
            content: htmlToText(termsData.content ?? ""),
            manger: termsData.updatedBy,
        };

        excelDownload({
            fileName: termsData.termsTitle ?? "",
            header,
            data: [excelData],
            colsWidth,
            // rowsHeight,
        });
    };

    const isCheckEdit = useCallback(() => {
        if (isEdit === "active" || isEdit === "future") {
            const customInitData = {
                ...initData,
                effectiveStartAt: initData && moment(initData.effectiveStartAt).format(DATA_FORMAT),
                effectiveEndAt: initData?.effectiveEndAt ? moment(initData.effectiveEndAt).format(DATA_FORMAT) : "",
            };

            const customTermsState = {
                ...termsData,
                effectiveStartAt: moment(termsData.effectiveStartAt).format(DATA_FORMAT),
                effectiveEndAt: termsData.effectiveEndAt ? moment(termsData.effectiveEndAt).format(DATA_FORMAT) : "",
            };
            const initTermsData = JSON.stringify(customInitData);
            const termsStateData = JSON.stringify(customTermsState);

            if (initTermsData === termsStateData) {
                return true;
            } else if (initTermsData !== termsStateData && termsData.effectiveEndAt && termsData.effectiveStartAt) {
                return false;
            }
        }
        return true;
    }, [initData, isEdit, termsData]);

    const isSaveButtonValid = useCallback(() => {
        const { effectiveStartAt, effectiveEndAt, content } = termsData;

        if (effectiveStartAt && effectiveEndAt && content) {
            return false;
        } else {
            return true;
        }
    }, [termsData]);

    // // 게시 기간 유효성 검사
    useEffect(() => {
        const { effectiveEndAt, effectiveStartAt } = termsData;
        if (!effectiveEndAt || !effectiveStartAt) {
            return;
        }

        const isSameOrAfter = moment(effectiveStartAt).isSameOrBefore(effectiveEndAt);

        if (!isSameOrAfter) {
            setIsAlertModalOpen({
                isOpenState: true,
                modalText: `게시기간이 올바르지 않아요.\n다시 확인해주세요.`,
            });
            setTermsData(prev => ({
                ...prev,
                effectiveEndAt: "",
            }));
            return;
        }
    }, [termsData.effectiveEndAt, termsData.effectiveStartAt]);

    return (
        <>
            <BaseModal modalType={modalType} isOpen modalWidth={61.25} disableModalPadding disableModalInnerGap handleClose={handleCloseModal}>
                <ModalTitleWrap>
                    <ModalTitle>
                        {title} 이력 {modalTitleState}
                    </ModalTitle>
                    <IconBox src={ICONS.MODAL_ICON.CLOSE} width={24} height={24} onClick={handleCloseModal} isCursorPointer />
                </ModalTitleWrap>
                <EditorWrap>
                    <PostDataWrap>
                        <Label>게시 기간</Label>
                        <RangeDateWrap>
                            <DatePicker
                                onChange={onChangeStartAt}
                                placeholder={"기간을 선택해주세요"}
                                format={DATA_FORMAT}
                                value={termsData.effectiveStartAt ? moment(termsData.effectiveStartAt, DATA_FORMAT) : undefined}
                                disabled={isEdit === "active" || isEdit === "past"}
                            />
                            <span />
                            <DatePicker
                                onChange={onChangeEndAt}
                                placeholder={"기간을 선택해주세요"}
                                format={DATA_FORMAT}
                                value={termsData.effectiveEndAt ? moment(termsData.effectiveEndAt, DATA_FORMAT) : undefined}
                                disabled={isEdit === "past"}
                            />
                        </RangeDateWrap>
                    </PostDataWrap>
                    {isEdit === "future" || !termsData.uuid ? (
                        <Editor initData={termsData.content ?? ""} onChange={handleEditorChange} placeHolder="이력 상세를 등록해주세요" editableHeight="320px" />
                    ) : (
                        <TermsData dangerouslySetInnerHTML={{ __html: termsData.content ?? "" }} />
                    )}
                </EditorWrap>
                <ButtonWrap isCreate={!termsUuid}>
                    {/* 삭제 가능할 경우 */}
                    {termsData.uuid && (
                        <ButtonInnerLeftWrap>
                            {isEdit === "future" && <Button buttonType="line_red" buttonSize="lager" text="삭제" onClick={handleDeleteButtonClick} />}
                            <Button buttonType="line_primary" buttonSize="lager" text="수정사유 확인" onClick={onReasonModificationButtonClick} disabled={(termsData.updateHistory?.length ?? 0) === 0} />
                            <StyledExcelButton iconsWidth={24} iconsHeight={24} onClick={onExcelDownload} />
                        </ButtonInnerLeftWrap>
                    )}
                    <div>
                        <Button buttonType="text" buttonSize="lager" style={{ marginRight: 8 }} text={"닫기"} onClick={handleCloseModal} />
                        {/* 수정 가능할 경우 */}
                        {(isEdit === "future" || isEdit === "active") && termsUuid && <Button buttonSize="lager" text={"저장하기"} onClick={onEditModalOpen} disabled={isCheckEdit()} />}
                        {/* 생성 */}
                        {!termsUuid && <Button buttonSize="lager" text={"등록하기"} onClick={handleCreateButtonClick} disabled={isSaveButtonValid()} />}
                    </div>
                </ButtonWrap>
            </BaseModal>
            {isReasonModificationModalOpen && <ReasonModificationModal updateHistory={termsData.updateHistory ?? []} category={"terms"} onClose={() => setIsReasonModificationModalOpen(false)} />}
            {isAlertModalOpen.isOpenState && (
                <OneButtonModal
                    title={isAlertModalOpen.modalText}
                    onConfirm={() =>
                        setIsAlertModalOpen({
                            isOpenState: false,
                            modalText: "",
                        })
                    }
                />
            )}
            {isReasonEditModal && <ReasonEditModal category="terms" onSave={handleReasonConfirm} onChangeReason={handleChangeReason} onClose={handleReasonClose} />}
            {isDeleteModalOpen && <TwoButtonModal title="삭제하시겠습니까?" firstButtonText="닫기" secondButtonText="삭제하기" onCancel={onDeleteModalClose} onConfirm={handleDeleteModalConfirm} handleClose={onDeleteModalClose} />}
        </>
    );
}

export default TermsEditorModal;

const ButtonWrap = styled.div<{ isCreate?: boolean }>`
    padding: 20px 32px;
    display: flex;
    justify-content: space-between;

    ${({ isCreate }) => {
        if (isCreate) {
            return css`
                margin-left: auto;
                display: block;
                justify-content: unset;
            `;
        }
    }}
`;

const ButtonInnerLeftWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const StyledExcelButton = styled(ExcelButton)`
    width: 159px;
    padding: 12px 20px;
    ${typo.BODY_7};
`;

const ModalTitleWrap = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 33px 32px 19px;
`;

const ModalTitle = styled.div`
    white-space: pre-line;
    color: ${colors.GRAY2};
    ${typo.HEADING_2};
`;

const EditorWrap = styled.div`
    /*  */

    padding: 32px 30px;
    border-top: 1px solid ${colors.GRAY9};
    border-bottom: 1px solid ${colors.GRAY9};
`;

const TermsData = styled.div`
    color: ${colors.GRAY6};
    word-break: break-all;
    white-space: pre-wrap;
    min-height: 300px;
    max-height: 450px;
    width: 100%;
    overflow-y: scroll;
`;

const PostDataWrap = styled.div`
    /*  */
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin-bottom: 24px;
`;

const Label = styled.div`
    /*  */
    display: flex;
    align-items: center;
    max-width: 200px;
    width: 100%;
    ${typo.HEADING_7};
    color: ${colors.GRAY6};
`;
